<script setup>
import { useBlockSafe } from '@/composables/useBlockSafe';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  items,
  hasTitle,
  hasItems,
  hasAnyContent,
} = useBlockSafe(props.data);
const activeTab = ref(props?.data?.items[0]?.id);
watch(activeTab, async () => {
  await nextTick();
  window.dispatchEvent(new Event('resize'));
});
const tabListElement = useTemplateRef('tabList');

const scrollToActiveTab = () => {
  nextTick(() => {
    if (!tabListElement.value) return;
    const activeTabEl = tabListElement.value.$el.querySelector(
      '[data-p-active="true"], .p-tab-active, .p-active',
    );

    if (activeTabEl) {
      activeTabEl.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  });
};

watch(activeTab, () => {
  scrollToActiveTab();
});

const onTabListResize = () => {
  tabListElement.value?.updateInkBar();
};

onMounted(() => {
  window.addEventListener('resize', onTabListResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onTabListResize);
});
</script>

<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div
        class="content-tabs"
        :class="
          data?.background_color ? `content-tabs--${backgroundColor}` : ''
        "
      >
        <h2
          v-if="hasTitle"
          class="mb-0 content-tabs__title h2"
          :class="data?.background_color === 'black' ? 'white' : 'black'"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="title"
        ></h2>

        <Tabs
          v-if="hasItems"
          v-model:value="activeTab"
          class="content-tabs__container"
          :scrollable="true"
        >
          <TabList
            ref="tabList"
            :class="[
              `content-tabs__list content-tabs__list--${backgroundColor} tabs-bg-${backgroundColor}`,
            ]"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
          >
            <Tab
              v-for="tab in items"
              :key="tab.id"
              :value="tab.id"
              :class="[
                `content-tabs__header content-tabs__header--${backgroundColor}`,
              ]"
            >
              {{ tab.title }}
            </Tab>
          </TabList>

          <TabPanels
            class="content-tabs__panels"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
          >
            <TabPanel
              v-for="tab in items"
              :key="tab.id"
              :value="tab.id"
              class="content-tabs__panel"
            >
              <div
                class="content-tabs__description body-1-regular"
                :class="data?.background_color === 'black' ? 'white' : 'black'"
                v-html="tab.description"
              ></div>

              <NuxtImg
                v-if="tab?.image && tab?.image?.src"
                :src="tab.image.src"
                :alt="tab.image.alt"
                :height="tab.image.height"
                :width="tab.image.width"
                class="content-tabs__image"
                loading="lazy"
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </div>
  </section>
</template>
<style scoped lang="scss">
.content-tabs {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 56px;
  @include media-breakpoint-up(sm) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }

  &__title {
    text-align: center;
  }
  &__container {
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
  }
  &__list {
    &:deep(.p-tablist-content) {
      border-bottom: 1px solid $divider;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-bottom-width, 1);
      }
    }
    &:deep(.p-tablist-active-bar) {
      height: 4px;
    }
    &:deep(.p-tablist-tab-list) {
      width: fit-content;
      margin: 0 auto;
    }
    @include media-breakpoint-up(sm) {
      display: flex;
      position: relative;
    }
    @include media-breakpoint-up(xl) {
      &:deep(.p-tablist-active-bar) {
        @include responsive-spacing(height, 4);
      }
    }
  }
  &__header {
    @include link-normal;
    color: $on-surface-variant-2;
    cursor: pointer;
    position: relative;
    padding: 16px;
    text-align: center;
    transition: color 0.2s ease;
    white-space: unset;
    width: auto;
    @include media-breakpoint-up(sm) {
      padding: 17px 16px 15px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding-top, 17);
      @include responsive-spacing(padding-right, 16);
      @include responsive-spacing(padding-left, 16);
      @include responsive-spacing(padding-bottom, 15);
    }
    &[data-p='active'] {
      color: $on-surface;
    }
  }
  &:deep(.p-tabpanel) {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    @include media-breakpoint-up(sm) {
      gap: 32px;
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: 6.08fr 5.92fr;
      gap: 0;
    }
  }
  &__description {
    @include media-breakpoint-up(md) {
      padding-right: 19.3%;
    }
    &:deep(ul) {
      list-style: none;
      padding-left: 24px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-left, 24);
      }
      li {
        margin-top: 8px;
        position: relative;
        &:before {
          background: url('/assets/icons/check-big-red.svg') no-repeat -1px 0px/100%
            auto;
          content: '';
          height: 16px;
          left: -24px;
          position: absolute;
          top: 4px;
          width: 16px;
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(left, -24);
            @include responsive-spacing(height, 16);
            @include responsive-spacing(top, 4);
            @include responsive-spacing(width, 16);
          }
        }
        @include media-breakpoint-up(sm) {
          margin-top: 16px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(margin-top, 16);
        }
      }
    }
  }
  &__image {
    border-radius: 4px;
    height: auto;
    width: 100%;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 4);
    }
  }
  &--white,
  &--gray {
    color: $on-surface-variant-2;
  }
  &--black {
    #{$parent} {
      &__header {
        color: $on-surface-variant-2;
        &[data-p='active'] {
          color: $white;
        }
      }
      &__list {
        &:deep(.p-tablist-content) {
          border-bottom-color: $on-surface-variant-2;
        }
      }
    }
  }
}
</style>
